<template>
  <div ref="containerRef" class="flex-center h-[512px] w-full max-w-screen">
    <div
      v-if="isTargetVisible"
      class="py-[24px] overflow-hidden relative w-full cursor-pointer group"
      @pointerover="isHover = true"
      @pointerleave="isHover = false"
    >
      <div class="flex will-change-transform" :style="style">
        <div ref="list" class="flex flex-shrink-[0] relative">
          <BrandItem v-for="item in brandList" :key="item.id" :item="item" />
        </div>
        <div class="flex flex-shrink-[0] relative">
          <BrandItem v-for="item in brandList" :key="item.id" :item="item" />
        </div>
      </div>
      <div
        ref="backRef"
        class="hidden group-hover:block nav-btn nav-btn-go left-[32px]"
        @click="() => switchDirection('back')"
        @pointerover="isHover = true"
        @pointerleave="isHover = false"
      >
        <img
          class="wh-full"
          :src="isBackHovered ? NavHoverIcon : NavIcon"
          alt="nav-icon"
        />
      </div>
      <div
        ref="goRef"
        class="hidden group-hover:block nav-btn right-[32px]"
        @click="() => switchDirection('go')"
        @pointerover="isHover = true"
        @pointerleave="isHover = false"
      >
        <img
          class="wh-full"
          :src="isGoHovered ? NavHoverIcon : NavIcon"
          alt="nav-icon"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useRafFn } from '@vueuse/core'
import BrandItem from './item.vue'
import type { SourcePageInfo } from '~/api/interface/landing'
import NavIcon from '~/assets/images/nav.webp'
import NavHoverIcon from '~/assets/images/nav-hover.webp'
import { useTargetVisible } from '~/composables/landing'

const OFFSET = 200

const props = defineProps<{ curItem: SourcePageInfo | null }>()
const containerRef = ref()
const { isTargetVisible } = useTargetVisible(containerRef)
const backRef = ref()
const isBackHovered = useElementHover(backRef)
const goRef = ref()
const isGoHovered = useElementHover(goRef)
const translateX = ref(0)
const direction = ref<'left' | 'right'>('left')

const [isAnimating, setAnimate] = useToggle(false)

const list = ref<HTMLDivElement>()
const isHover = ref(false)
const visibility = useElementVisibility(containerRef)
const { width, height } = useElementSize(list)
const pixelsMovedPerFrameOnIdle = 1.5
// const pixelsMovedPerFrameOnHover = 0.5
const brandList = computed(() => {
  const mediaArr = props.curItem?.resource[0].mediaArray || []
  const btnArr = props.curItem?.btn || []
  return mediaArr.map((media, index) => {
    return {
      ...media,
      btn: btnArr[index] || null,
    }
  })
})
useRafFn(() => {
  if (isAnimating.value && visibility.value) {
    const bound = width.value
    if (bound) {
      const pixelsMovedPerFrame = pixelsMovedPerFrameOnIdle
      const newTranslateX = translateX.value + pixelsMovedPerFrame

      if (newTranslateX > bound) {
        translateX.value = 0
      } else {
        translateX.value = newTranslateX
      }
    }
  }
})

const style = computed(() => {
  const bound = width.value
  const newValue =
    direction.value === 'left' ? -translateX.value : translateX.value - bound
  return {
    transform: `translateX(${newValue}px)`,
  }
})

const switchDirection = (newDirection: 'go' | 'back') => {
  // 先暂停
  setAnimate(false)

  if (newDirection === 'go') {
    // 当前方向快速滚动一段距离
    const targetX = translateX.value + OFFSET
    // const bound = list.value?.clientWidth || 1920
    const bound = width.value
    if (targetX > bound) {
      translateX.value = 0
    } else {
      translateX.value = targetX
    }
  } else {
    // back 快速往右滚动一段距离
    const targetX = translateX.value - OFFSET
    const bound = width.value
    if (targetX < 0) {
      translateX.value = bound
    } else {
      translateX.value = targetX
    }
  }
}

watch(isHover, (hovered) => {
  if (hovered) {
    // 直接暂停
    setAnimate(false)
    return
  }
  const paused = !isAnimating.value
  if (paused) {
    setAnimate(true)
  }
})

watch(visibility, (visible) => setAnimate(visible))
</script>
<style lang="scss" scoped>
.nav-btn {
  width: 60px;
  height: 60px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 20px 50px -6px rgba(24, 24, 28, 0.19);
  backdrop-filter: blur(4px);
  border-radius: 30px;
  cursor: pointer;
  z-index: 2;
}
.nav-btn-go {
  transform: translateY(-50%) scaleX(-1);
}
</style>
